/**
 * Who on a patient's care team holds a given role.
 *
 * `CareTeam.participant[].role` carries exactly one value in this system —
 * `callable` — so a participant's *provider* role (PN, CC, NP …) is never on the
 * CareTeam itself. It lives on the practitioner's `PractitionerRole.code`, and
 * the two have to be cross-referenced. Both the routing bots and the Orbit
 * patient list need that cross-reference, which is why it lives here rather than
 * in either package.
 */

import { getReferenceString } from '@medplum/core';
import { CareTeam, Practitioner, PractitionerRole, Reference } from '@medplum/fhirtypes';
import { ProviderRole } from './constants';

/** Practitioners whose active `PractitionerRole` carries `role`, as references. */
export function getPractitionersByRole(
  practitionerRoleList: PractitionerRole[],
  role: ProviderRole
): Reference<Practitioner>[] {
  return practitionerRoleList
    .filter((pr) => pr.active !== false)
    .filter((pr) => pr.code?.some((c) => c.coding?.some((c) => c.code === role)))
    .map((pr) => pr.practitioner as Reference<Practitioner>);
}

/**
 * True when every one of the practitioner's roles is `active: false` (an offboarded
 * provider). A practitioner with no role at all is not treated as inactive.
 */
export function isInactivePractitioner(
  practitionerRoleList: PractitionerRole[],
  practitioner: Reference<Practitioner>
): boolean {
  const reference = getReferenceString(practitioner);
  const roles = practitionerRoleList.filter(
    (pr) => pr.practitioner && getReferenceString(pr.practitioner) === reference
  );
  return roles.length > 0 && roles.every((pr) => pr.active === false);
}

/**
 * Care-team members who are in `rolePool`, in participant order.
 *
 * `rolePool` is the reference strings of every practitioner holding the role
 * (from `getPractitionersByRole`). Participant order is preserved because the
 * routing bots treat the first PN on the team as the assigned one, and a list
 * column that reordered them would disagree with routing.
 */
export function getCareTeamMembersInPool(
  careTeam: Pick<CareTeam, 'participant'> | undefined,
  rolePool: ReadonlySet<string>
): string[] {
  const members: string[] = [];

  for (const participant of careTeam?.participant ?? []) {
    const reference = participant.member?.reference;
    if (reference && rolePool.has(reference) && !members.includes(reference)) {
      members.push(reference);
    }
  }

  return members;
}

/** The reference strings of a role pool, ready for `getCareTeamMembersInPool`. */
export function toRolePool(practitioners: Reference<Practitioner>[]): Set<string> {
  return new Set(
    practitioners.map((reference) => getReferenceString(reference)).filter((value): value is string => Boolean(value))
  );
}
